<template>
  <BaseInputSelect
    ref="inputSelect"
    v-model="selectedOptions"
    :aria-label="ariaLabel"
    :clearable="clearable"
    data-intercom-target="stream-picker"
    data-testid="stream-picker"
    :disabled="disabled"
    :dropdown-text="dropdownText"
    emit-value
    :hide-dropdown-button="useKeywordSearch"
    :input-text="inputText"
    is-active-keyboard-only
    :is-grid="hasRowActions"
    is-object-selection
    :is-roving-focus-enabled="!useKeywordSearch"
    :multiple="!single"
    new-design
    option-label="label"
    option-prop="value"
    :optional="optional"
    :options="filteredListOptions"
    :placeholder="inputPlaceholder"
    :popper-width="popperWidth"
    :prevent-clear-input-on-submit="useKeywordSearch"
    :search-el="searchEl"
    :selected-text="selectedText"
    :teleport="teleport"
    :use-input="useKeywordSearch"
    :validations="validations"
    @click="$emit('click')"
    @dropdown-closed="resetChanges"
    @filter="onFilter"
    @selected="$emit('option-click', $event)"
  >
    <template
      v-if="useKeywordSearch"
      #empty
    >
      <div />
    </template>

    <template
      v-if="requiresPrependSlot"
      #prepend
    >
      <div
        v-if="single && selectedOptions"
        v-show="selectedOptions"
        class="stream-icon no-shrink col-auto"
        :style="{ backgroundColor: iconColor }"
      />
      <QIcon
        v-else-if="useKeywordSearch"
        color="ds-denim-4"
        name="ion-search"
        size="xs"
      />
    </template>
    <template
      v-if="!useKeywordSearch"
      #beforeOptions
    >
      <div
        class="before-options"
        @click.stop
      >
        <BaseInputText
          ref="searchInput"
          v-model="filter"
          :aria-label="$t('global.search')"
          autocomplete="off"
          borderless
          class="input"
          clearable
          :debounce="250"
          new-design
          :placeholder="$t('global.search')"
          @focus="$emit('search-focus')"
          @vue:mounted="setSearchEl"
          @vue:unmounted="clearSearchEl"
        >
          <template #prepend>
            <BaseImageIcon
              class="text-ds-denim-4"
              fallback-icon="ion-search"
            />
          </template>
        </BaseInputText>
        <slot name="optionsText">
          <div
            v-if="!single && maxSelected > 0 && !hideMaxSelected"
            class="text-ds-denim-6 q-pa-sm stream-dropdown-description-font-size bold"
          >
            {{ $t("stream_picker.max_selected", { maxSelected }) }}
          </div>
        </slot>
      </div>
    </template>
    <template #option="{ option, selected, active, keyboardActive, isFocused }">
      <QIntersection class="intersection">
        <div
          class="list-option row full-width no-wrap items-center"
          :class="{
            active: active,
            'active--keyboard': keyboardActive,
            selected: selected && single,
            'group-option': option.isGroup,
            'tw:hover:bg-transparent! tw:cursor-default!':
              option.isSection || option.isGroup,
            [option.className]: option.className,
          }"
          :data-testid="
            option.isGroup
              ? 'stream-picker-option-group'
              : 'stream-picker-option'
          "
        >
          <BaseStreemTooltip
            v-if="streamDisabledMessage(option)"
            anchor="center middle"
            no-caret
            self="center left"
          >
            <div style="font-size: 11px">
              {{ streamDisabledMessage(option) }}
            </div>
          </BaseStreemTooltip>
          <slot
            v-else-if="option.disabled"
            name="disabled"
          />
          <!-- With row actions the list is a grid (see AutocompleteList), and
               every part of the row sits in its own cell. This one holds the
               stream and is where the arrow keys land. -->
          <div
            class="tw:flex tw:min-w-0 tw:flex-1 tw:items-center"
            :role="hasRowActions ? 'gridcell' : undefined"
            :tabindex="hasRowActions && isFocusableRow(option) ? -1 : undefined"
          >
            <div v-if="option.isTag">
              <BaseIcon
                icon="tag"
                :style="{ color: option.color }"
              />
            </div>
            <div
              v-else-if="!option.isSection"
              class="stream-icon no-shrink col-auto"
              :style="{ backgroundColor: option.color }"
            />
            <div
              class="ellipsis col-grow col-shrink overflow-hidden"
              :class="{ 'tw:pl-2': !option.isSection }"
            >
              {{ option.label }}
            </div>
          </div>
          <div
            v-if="hasRowActions"
            class="tw:flex tw:flex-none tw:items-center"
            role="gridcell"
          >
            <slot
              :active="active"
              :hide-list="hideList"
              :is-focused="isFocused"
              name="append"
              :option="option"
              :selected="selected"
            />
          </div>
          <slot
            v-else
            :active="active"
            :hide-list="hideList"
            :is-focused="isFocused"
            name="append"
            :option="option"
            :selected="selected"
          />
          <div
            v-if="!single"
            class="checkbox-left no-shrink col-auto"
            :role="hasRowActions ? 'gridcell' : undefined"
          >
            <BaseCheckbox
              v-if="!option.isGroup"
              :class="`color-${option.checkboxColor}` || checkboxColor"
              :model-value="selected"
              new-design
              :tabindex="-1"
              @click="$emit('checkbox-toggled', option.label)"
            />
          </div>
        </div>
      </QIntersection>
    </template>
    <template
      v-if="!single"
      #afterOptions
    >
      <div
        class="row ok-row q-pa-xs border-ds-denim-2 full-width"
        :class="{ 'cancel-apply-button-group': cancelApply }"
      >
        <BaseButton
          v-if="cancelApply"
          color="tertiary"
          data-testid="stream-picker-cancel-button"
          :label="$t('global.cancel')"
          new-design
          @click.stop="onCancel"
        />
        <BaseButton
          :class="{ 'full-width': !cancelApply }"
          color="primary"
          data-testid="stream-picker-confirm-button"
          :disabled="invalid"
          :label="cancelApply ? $t('global.apply') : $t('global.confirm')"
          new-design
          @click.stop="onConfirm"
        />
        <BaseStreemTooltip v-if="invalid">
          {{ $t("stream_picker.min_selected", { minSelected }) }}
        </BaseStreemTooltip>
      </div>
    </template>
  </BaseInputSelect>
</template>

<script>
import { keyBy } from "lodash-es";
import { computed } from "vue";

import { getLocaleText } from "shared/boot/i18n";
import {
  BaseButton,
  BaseIcon,
  BaseImageIcon,
  BaseInputSelect,
  BaseInputText,
  BaseStreemTooltip,
} from "shared/components/base";
import BaseCheckbox from "shared/components/core/forms/BaseCheckbox.vue";
import { streamTypes } from "shared/constants";
import { useStreamsStore } from "shared/stores/streams";

export default {
  name: "StreamPicker",
  components: {
    BaseButton,
    BaseIcon,
    BaseCheckbox,
    BaseInputSelect,
    BaseInputText,
    BaseImageIcon,
    BaseStreemTooltip,
  },
  props: {
    modelValue: {
      type: [Array, Number, String],
      default: null,
    },
    ariaLabel: {
      type: String,
      default: "",
    },
    inputText: {
      type: String,
      default: "",
    },
    validations: {
      type: Array,
      default: () => [],
    },
    optional: {
      type: Boolean,
    },
    sharedStreams: {
      type: Array,
      default: () => [],
      validator: (streamOptions) =>
        streamOptions.every(
          (streamOption) => streamOption.id && streamOption.label
        ),
    },
    minSelected: {
      type: Number,
      default: 1,
    },
    maxSelected: {
      type: Number,
      default: 12,
    },
    popperWidth: {
      type: [Number, null],
      default: 300,
    },
    selectedText: {
      type: Function,
      default: null,
    },
    checkboxColor: {
      type: String,
      default: "primary",
    },
    single: {
      type: Boolean,
    },
    includeSocialBookmarkStreams: {
      type: Boolean,
    },
    disabled: {
      type: Boolean,
    },
    clearable: {
      type: Boolean,
    },
    keywordSearch: {
      type: Boolean,
    },
    socialStreams: {
      type: Boolean,
    },
    hideBookmarkStreams: {
      type: Boolean,
    },
    disabledStreamIds: {
      type: Array,
      default: () => [],
    },
    teleport: {
      type: String,
      default: null,
    },
    hideMaxSelected: {
      type: Boolean,
    },
    cancelApply: {
      type: Boolean,
    },
    disabledRules: {
      type: Array,
      default: null,
    },
    streamOptions: {
      type: Array,
      default: null,
    },
  },
  emits: [
    "click",
    "search-focus",
    "checkbox-toggled",
    "confirm",
    "update:modelValue",
    "cancel",
    "option-click",
    "update:filter",
  ],
  setup(props) {
    const streamsStore = useStreamsStore();

    const {
      groupedTraditionalStreams,
      groupedSocialStreams,
      groupedSocialBookmarkStreams,
    } = streamsStore;

    const streamOptions = computed(() => {
      const sharedStreams = keyBy(props.sharedStreams, "id");
      const accessibleStreams = new Set();
      const options = [];

      const groupedStreams = props.socialStreams
        ? groupedSocialStreams()
        : groupedTraditionalStreams();

      groupedStreams
        .filter((group) => {
          if (props.hideBookmarkStreams) {
            return group.stream_type !== streamTypes.bookmarkStream;
          }

          return true;
        })
        .forEach((group) => {
          const groupOption = {
            value: group.id,
            label: group.label,
            color: group.color,
            isGroup: true,
            isTag: group.stream_type === streamTypes.bookmarkStream,
            options: [],
          };

          group.streams.forEach((stream) => {
            groupOption.options.push({
              value: stream.id,
              label: stream.label,
              isTag: stream.type === streamTypes.bookmarkStream,
              color: stream.color || group.color,
              primary: stream.primary,
              streamType: stream.type,
              canModify: stream.can_modify,
            });

            accessibleStreams.add(stream.id);
          });

          options.push(groupOption);
        });

      const sharedStreamOptions = [];

      Object.values(sharedStreams).forEach((stream) => {
        if (!accessibleStreams.has(stream.id)) {
          sharedStreamOptions.push({
            value: stream.id,
            label: stream.label,
            color: stream.color,
          });
        }
      });

      if (
        props.includeSocialBookmarkStreams &&
        groupedSocialBookmarkStreams().length
      ) {
        groupedSocialBookmarkStreams().forEach((group) => {
          const groupOption = {
            value: group.id,
            label: group.label,
            color: group.color,
            isGroup: true,
            isTag: true,
            options: [],
          };

          group.streams.forEach((stream) => {
            groupOption.options.push({
              value: stream.id,
              label: stream.label,
              isTag: true,
              color: stream.color || group.color,
            });
          });

          options.push(groupOption);
        });
      }

      const sharedGroup = {
        value: 0,
        label: getLocaleText("stream_picker.shared_report_streams"),
        color: null,
        isGroup: true,
        options: sharedStreamOptions,
      };

      options.unshift(sharedGroup);

      return options;
    });

    return {
      defaultStreamOptions: streamOptions,
    };
  },
  data() {
    return {
      filter: "",
      localSelected: [],
      searchEl: null,
      unsavedChanges: false,
    };
  },
  computed: {
    // Controls inside a row make the list a grid, which relies on roving focus.
    hasRowActions() {
      return Boolean(this.$slots.append) && !this.useKeywordSearch;
    },
    requiresPrependSlot() {
      return (this.single && this.selectedOptions) || this.useKeywordSearch;
    },
    inputPlaceholder() {
      return this.useKeywordSearch
        ? this.$t("stream_picker.keyword_search_placeholder")
        : "";
    },
    resolvedStreamOptions() {
      return this.streamOptions ?? this.defaultStreamOptions;
    },
    selectableOptions() {
      return this.resolvedStreamOptions.flatMap(({ options }) => options);
    },
    selectedValues() {
      return (this.unsavedChanges ? this.localSelected : this.modelValue) || [];
    },
    selectedOptions: {
      get() {
        if (this.single) {
          return this.selectableOptions.find(
            (option) => this.selectedValues === option.value
          )?.value;
        }

        return this.selectableOptions.filter((option) =>
          this.selectedValues.includes(option.value)
        );
      },
      set(value) {
        if (this.single) {
          this.emitChanges(value);
        } else {
          this.localSelected = value || [];
          this.unsavedChanges = true;
        }
      },
    },
    dropdownText() {
      if (this.single) {
        if (this.modelValue) {
          return this.selectedOptions.label;
        }

        if (this.useKeywordSearch) {
          return this.filter;
        }

        return this.socialStreams
          ? this.$t("stream_picker.select_social_stream")
          : this.$t("stream_picker.select_mention_stream");
      }

      const selected = this.selectedOptions?.length;

      if (this.socialStreams) {
        return selected
          ? this.$t("stream_picker.selected_social_stream", {
              count: selected,
            })
          : this.$t("stream_picker.select_social_stream", { count: 2 });
      }

      return selected
        ? this.$t("stream_picker.selected_mention_stream", {
            count: selected,
          })
        : this.$t("stream_picker.select_mention_stream", { count: 2 });
    },
    filteredListOptions() {
      const filter = this.filter.toLowerCase();

      const filteredListOptions = [];

      this.resolvedStreamOptions.forEach((group) => {
        const groupOptions = this.filterOptions(group.options, filter);

        if (groupOptions.length || (group.isSection && !filter)) {
          filteredListOptions.push(
            { ...group, notSelectable: true },
            ...groupOptions
          );
        }
      });

      return filteredListOptions;
    },
    invalid() {
      return this.single
        ? !this.selectedValues
        : Boolean(
            this.minSelected && this.selectedValues.length < this.minSelected
          );
    },
    useKeywordSearch() {
      return this.single && this.keywordSearch;
    },
    iconColor() {
      if (!this.single) return "";

      const selectedOption = this.selectableOptions.find(
        (option) => this.selectedValues === option.value
      );

      return selectedOption ? selectedOption.color : "";
    },
  },
  methods: {
    // The rows AutocompleteList lets the arrow keys land on.
    isFocusableRow(option) {
      return !option.isGroup && !option.notSelectable && !option.disabled;
    },
    filterOptions(options, filter) {
      const filteredOptions = [];

      options.forEach((option) => {
        if (option.label.toLowerCase().includes(filter)) {
          filteredOptions.push({
            ...option,
            disabled:
              this.isDisabled(option) ||
              Boolean(this.streamDisabledMessage(option)),
          });
        }
      });

      return filteredOptions;
    },
    isDisabled(option) {
      if (this.disabledStreamIds.includes(option.value)) {
        return true;
      }

      return this.single
        ? false
        : this.maxSelected &&
            this.selectedValues.length >= this.maxSelected &&
            !this.isSelected(option);
    },
    streamDisabledMessage(option) {
      if (!this.disabledRules) return null;

      const matchedRule = this.disabledRules.find((rule) =>
        rule.condition(option)
      );

      return matchedRule ? matchedRule.message : null;
    },
    isSelected(option) {
      return this.single
        ? this.selectedOptions === option
        : this.selectedOptions.includes(option);
    },
    onConfirm() {
      this.$emit("confirm");

      if (this.unsavedChanges) {
        this.emitChanges(this.localSelected);
      } else {
        this.$refs.inputSelect.hideList();
      }
    },
    onCancel() {
      this.resetChanges();

      this.$refs.inputSelect.hideList();
    },
    emitChanges(value) {
      this.$emit("update:modelValue", value);
      this.unsavedChanges = false;
      this.$refs.inputSelect.hideList();
    },
    resetChanges() {
      if (this.unsavedChanges) {
        this.$emit("cancel");
      }

      this.localSelected = [];
      this.unsavedChanges = false;
    },
    onFilter(value) {
      this.filter = value;

      this.$emit("update:filter", value);
    },
    setSearchEl() {
      this.searchEl = this.$refs.searchInput?.input ?? null;
    },
    clearSearchEl() {
      this.searchEl = null;
    },
    /** @public */
    focus() {
      this.$refs.inputSelect.focus();
    },
    /** @public */
    validate() {
      return this.$refs.inputSelect.validate();
    },
    hideList() {
      this.$refs.inputSelect.hideList();
    },
  },
};
</script>

<style lang="scss" scoped>
.stream-icon {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: #757575;
}

.checkbox-left {
  margin-left: auto;
}

.text-label {
  color: $ds-denim-9;
}

.input-select {
  :deep(.list) {
    .list-body {
      max-height: 200px;
    }
  }

  :deep(.list-option) {
    padding-right: 8px;

    label.checkbox {
      margin: 0;
    }
  }
}

.intersection {
  height: 34px;
  width: 100%;
}

.cancel-apply-button-group {
  border-top: 1px solid $ds-denim-2;
  padding-top: 10px;
}
</style>
